import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  IconButton,
  Typography,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
} from '@mui/material';
import { Close, ZoomIn, ZoomOut, FileDownload, Psychology } from '@mui/icons-material';
import { useMutation, useApolloClient } from '@apollo/client';
import { TrainingSample, Disease, BodyZoneId, ViewType, LLMAnalysis } from '../types';
import { DiseaseSelector } from './DiseaseSelector';
import { UPDATE_TRAINING_SAMPLE } from '../graphql/operations';
import { AnatomySelector } from './AnatomySelector';
import { streamClinicalAnalysis } from '../services/clinicalAnalysisService';

interface FullScreenImageViewerProps {
  open: boolean;
  onClose: () => void;
  imageUrl?: string;
  sample: TrainingSample;
  onSampleUpdated?: () => void; // Callback to refresh data after update
}

export const FullScreenImageViewer: React.FC<FullScreenImageViewerProps> = ({
  open,
  onClose,
  imageUrl,
  sample,
  onSampleUpdated,
}) => {
  const client = useApolloClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [zoom, setZoom] = React.useState(1);
  const [selectedDisease, setSelectedDisease] = useState<Disease | null>(sample.disease || null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Clinical analysis state
  const [clinicalAnalysis, setClinicalAnalysis] = useState('');
  const [isClinicalAnalyzing, setIsClinicalAnalyzing] = useState(false);
  const [clinicalError, setClinicalError] = useState<string | null>(null);
  const [showClinicalModal, setShowClinicalModal] = useState(false);

  // Auto-save mutation for disease labeling
  const [updateDiseaseLabel] = useMutation(UPDATE_TRAINING_SAMPLE, {
    onCompleted: (data) => {
      if (data.updateTrainingSample === true) {
        setSuccessMessage('Disease label updated!');
        setTimeout(() => setSuccessMessage(''), 2000);
        // Intentionally avoid triggering parent refetch to preserve pagination/scroll state
      }
    },
    onError: (error) => {
      console.error('Error updating disease label:', error);
    }
  });

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));

  const handleDownload = async () => {
    try {
      if (!imageUrl) return;

      let ext = 'jpg';
      try {
        const headResp = await fetch(imageUrl, { method: 'HEAD' });
        const ct = headResp.headers.get('Content-Type') || headResp.headers.get('content-type');
        if (ct) {
          if (ct.includes('png')) ext = 'png';
          else if (ct.includes('webp')) ext = 'webp';
          else if (ct.includes('gif')) ext = 'gif';
          else if (ct.includes('jpeg') || ct.includes('jpg')) ext = 'jpg';
        }
      } catch (e) {
        // Ignore HEAD errors and fall back to GET
      }

      const resp = await fetch(imageUrl);
      const blob = await resp.blob();
      if (blob.type) {
        if (blob.type.includes('png')) ext = 'png';
        else if (blob.type.includes('webp')) ext = 'webp';
        else if (blob.type.includes('gif')) ext = 'gif';
        else if (blob.type.includes('jpeg') || blob.type.includes('jpg')) ext = 'jpg';
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const base = sample?.id ? `sample_${sample.id.slice(-8)}` : 'image';
      a.download = `${base}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image', error);
    }
  };

  const handleClinicalAnalysis = async () => {
    if (!sample.id) return;
    
    setShowClinicalModal(true);
    setIsClinicalAnalyzing(true);
    setClinicalAnalysis('');
    setClinicalError(null);

    try {
      for await (const chunk of streamClinicalAnalysis(sample.id)) {
        if ('done' in chunk && chunk.done) {
          if ('error' in chunk) {
            setClinicalError(chunk.error);
          } else {
            setClinicalAnalysis(chunk.response);
          }
        } else {
          // Stream chunks as they arrive
          setClinicalAnalysis(prev => prev + chunk);
        }
      }
    } catch (error) {
      console.error('Clinical analysis failed:', error);
      setClinicalError(error instanceof Error ? error.message : 'Analysis failed');
    } finally {
      setIsClinicalAnalyzing(false);
    }
  };

  React.useEffect(() => {
    if (open) {
      setZoom(1); // Reset zoom when opening
      setSelectedDisease(sample.disease || null); // Update selected disease when sample changes
    }
  }, [open, sample.disease]);

  const hasBodyZones = Array.isArray(sample.body_zone_ids) && sample.body_zone_ids.length > 0;

  const selectedBodyZones = React.useMemo(() => {
    return new Set<BodyZoneId>((sample.body_zone_ids ?? []) as BodyZoneId[]);
  }, [sample.body_zone_ids]);

  const llmAnalysis = React.useMemo<LLMAnalysis | null>(() => {
    if (!sample.llm_analysis) return null;
    
    if (typeof sample.llm_analysis === 'object') {
      return sample.llm_analysis as LLMAnalysis;
    }

    try {
      let parsed = JSON.parse(sample.llm_analysis);
      // Handle double-encoded JSON (parse again if still a string)
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      return parsed as LLMAnalysis;
    } catch (error) {
      console.warn('Failed to parse llm_analysis', error, sample.llm_analysis);
      return null;
    }
  }, [sample.llm_analysis]);

  const renderDifferentialDiagnoses = () => {
    if (!sample.differential_diagnoses) return null;

    try {
      let ddx = JSON.parse(sample.differential_diagnoses);
      
      // Parse if it's a string
      if (typeof ddx === 'string') {
        ddx = JSON.parse(ddx);
      }

      // Handle different possible structuresi
      let diagnosesList: any[] = [];
      
      if (Array.isArray(ddx)) {
        diagnosesList = ddx;
      }

      if (diagnosesList.length > 0) {
        return (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
              Differential Diagnoses ({diagnosesList.length})
            </Typography>
            {diagnosesList.map((diagnosis: any, index: number) => {
              // Handle different field names for the diagnosis
              const name = diagnosis.label ||
                          diagnosis.condition || 
                          diagnosis.name || 
                          diagnosis.diagnosis || 
                          diagnosis.disease ||
                          (typeof diagnosis === 'string' ? diagnosis : 'Unknown');
              
              const confidence = diagnosis.confidence || 
                               diagnosis.probability || 
                               diagnosis.score;

              return (
                <Box key={index} sx={{ mb: 1, p: 1, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {name}
                  </Typography>
                  {confidence && (
                    <Typography variant="caption" color="text.secondary">
                      Confidence: {index === 0 ? (confidence * 100).toFixed(2) : confidence.toFixed(2)}%
                    </Typography>
                  )}
                  {diagnosis.reasoning && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      {diagnosis.reasoning}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      }
    } catch (error) {
      console.error('Error parsing differential diagnoses:', error, sample.differential_diagnoses);
      
      // Fallback: show raw data if parsing fails
      return (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Differential Diagnoses (Raw)
          </Typography>
          <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {typeof sample.differential_diagnoses === 'string' 
                ? sample.differential_diagnoses 
                : JSON.stringify(sample.differential_diagnoses, null, 2)}
            </Typography>
          </Box>
        </Box>
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '100vw',
          height: '100vh',
          margin: 0,
          maxWidth: 'none',
          maxHeight: 'none',
          borderRadius: 0,
          bgcolor: 'black',
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative', height: '100%', overflow: 'hidden' }}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 3,
            bgcolor: 'rgba(0,0,0,0.5)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
          }}
        >
          <Close />
        </IconButton>

        {/* Zoom Controls */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 3,
            display: 'flex',
            gap: 1,
          }}
        >
          <IconButton
            onClick={handleZoomOut}
            sx={{
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <ZoomOut />
          </IconButton>
          <IconButton
            onClick={handleZoomIn}
            sx={{
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <ZoomIn />
          </IconButton>
          <IconButton
            onClick={handleDownload}
            sx={{
              bgcolor: 'rgba(0,0,0,0.5)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
            title="Download"
          >
            <FileDownload />
          </IconButton>
          <IconButton
            onClick={handleClinicalAnalysis}
            sx={{
              bgcolor: 'rgba(76,175,80,0.7)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(76,175,80,0.9)' },
            }}
            title="Clinical Analysis (Gemini)"
          >
            <Psychology />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Image Section */}
          <Box
            sx={{
              flex: isMobile ? 1 : '1 1 70%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'black',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Training sample"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  transform: `scale(${zoom})`,
                  transition: 'transform 0.2s ease',
                  cursor: zoom < 3 ? 'zoom-in' : 'zoom-out',
                }}
                onClick={zoom < 3 ? handleZoomIn : handleZoomOut}
              />
            ) : (
              <Typography color="white">No image available</Typography>
            )}

            {hasBodyZones && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 16,
                  bottom: 16,
                  width: isMobile ? 160 : 300,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: 3,
                }}
              >
                <AnatomySelector
                  viewType={ViewType.front}
                  selectedZones={selectedBodyZones}
                  onZoneSelected={() => {}}
                />
                <AnatomySelector
                  viewType={ViewType.back}
                  selectedZones={selectedBodyZones}
                  onZoneSelected={() => {}}
                />
              </Box>
            )}
          </Box>

          {/* Info Panel - Hidden on mobile */}
          {!isMobile && (
            <Paper
              sx={{
                flex: '0 0 350px',
                bgcolor: 'background.paper',
                p: 3,
                overflow: 'auto',
                borderRadius: 0,
              }}
            >
              <Typography variant="h5" gutterBottom sx={{ fontSize: '1.25rem' }}>
                Sample #{sample.id.slice(-8)}
              </Typography>

              {/* Success Message */}
              {successMessage && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.dark">
                    ✓ {successMessage}
                  </Typography>
                </Box>
              )}

              {/* Disease Selector */}
              <Box sx={{ mb: 3 }}>
                <DiseaseSelector
                  value={selectedDisease}
                  onChange={async (disease) => {
                    setSelectedDisease(disease);
                    
                    // Auto-save the disease selection and correct diagnosis
                    try {
                      await updateDiseaseLabel({
                        variables: {
                          id: sample.id,
                          input: { 
                            label_id: disease?.id || null,
                            correct_diagnosis: disease?.name || ''
                          }
                        },
                        optimisticResponse: { updateTrainingSample: true }
                      });

                      // Update Apollo cache to avoid refetch and preserve list pagination/scroll
                      try {
                        const cacheId = client.cache.identify({ __typename: 'TrainingSample', id: sample.id });
                        if (cacheId) {
                          client.cache.modify({
                            id: cacheId,
                            fields: {
                              label_id: () => (disease?.id || null),
                              correct_diagnosis: () => (disease?.name || ''),
                            },
                          });
                        }
                      } catch (cacheErr) {
                        console.warn('Failed to update cache for TrainingSample', sample.id, cacheErr);
                      }
                    } catch (error) {
                      console.error('Failed to auto-save disease label:', error);
                    }
                  }}
                  label="Disease Label"
                  placeholder="Search for a disease..."
                  fullWidth
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* AI Prediction */}
              {sample.prediction && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    AI Prediction
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {sample.prediction}
                  </Typography>
                  {sample.confidence && (
                    <Typography variant="body2" color="text.secondary">
                      Confidence: {(sample.confidence * 100).toFixed(1)}%
                    </Typography>
                  )}
                </Box>
              )}

              {/* Staff Diagnosis */}
              {llmAnalysis?.diagnosis && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Staff Diagnosis
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    ✓ {llmAnalysis.diagnosis} - {llmAnalysis.confidence}
                  </Typography>
                </Box>
              )}

              {/* AI Why */}
              {llmAnalysis?.why && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    AI Why
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {llmAnalysis.why}
                  </Typography>
                </Box>
              )}

              {/* AI Question */}
              {llmAnalysis?.questions && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    AI Question
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {llmAnalysis.questions}
                  </Typography>
                </Box>
              )}



              {/* AI Summary */}
              {sample.staff_notes && !llmAnalysis&& (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Staff Notes
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {sample.staff_notes}
                  </Typography>
                </Box>
              )}

              {/* Symptoms Description */}
              {sample.symptoms_description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Symptoms Description
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    "{sample.symptoms_description}"
                  </Typography>
                </Box>
              )}

              {/* Disease Label */}
              {sample.disease && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Disease Label
                  </Typography>
                  <Box sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {sample.disease.name}
                    </Typography>
                    {sample.disease.icd10_code && (
                      <Typography variant="caption" color="text.secondary">
                        ICD-10: {sample.disease.icd10_code}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

               {/* Differential Diagnoses */}
               {renderDifferentialDiagnoses()}

            </Paper>
          )}
        </Box>

        {/* Mobile Info Overlay */}
        {isMobile && (
          <Paper
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              p: 2,
              bgcolor: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(4px)',
              maxHeight: '40%',
              overflow: 'auto',
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Sample #{sample.id.slice(-8)}
            </Typography>
            
            {sample.prediction && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>AI:</strong> {sample.prediction}
                {sample.confidence && ` (${(sample.confidence * 100).toFixed(1)}%)`}
              </Typography>
            )}

            {sample.disease && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Label:</strong> {sample.disease.name}
              </Typography>
            )}

            {sample.ai_summary && (
              <Typography variant="caption" color="text.secondary">
                {sample.ai_summary}
              </Typography>
            )}
          </Paper>
        )}

        {/* Clinical Analysis Modal */}
        <Dialog
          open={showClinicalModal}
          onClose={() => setShowClinicalModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'background.paper',
              minHeight: '400px',
            }
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Psychology sx={{ color: 'success.main' }} />
            <Typography variant="h6" component="span">
              Clinical Dermatology Assessment
            </Typography>
            {isClinicalAnalyzing && <CircularProgress size={20} sx={{ ml: 'auto' }} />}
          </DialogTitle>
          <DialogContent dividers>
            {clinicalError ? (
              <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                <Typography color="error.dark">
                  <strong>Error:</strong> {clinicalError}
                </Typography>
              </Box>
            ) : (
              <>
                {clinicalAnalysis && (
                  <Paper sx={{ p: 2, bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
                    <Typography 
                      variant="body1" 
                      component="div" 
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      {clinicalAnalysis}
                    </Typography>
                  </Paper>
                )}

                {/* Loading State */}
                {isClinicalAnalyzing && !clinicalAnalysis && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Analyzing case with Gemini AI dermatologist...
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowClinicalModal(false)} variant="contained" color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};